import { localeHref, localizeBodyHrefs } from './locale-href.js';
import { ledeHtml, ledeText } from './rich-text.js';
import { formatPostDate, heroSrcset, photoCreditHtml } from './country-page.js';
import { anchorSections, TOC_MIN_SECTIONS } from './blog-structure.js';
function esc(s) {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
const FIELDS = ['title', 'description', 'h1', 'lede', 'body', 'category', 'readTime', 'date', 'lastUpdated', 'hero'];
/**
 * The post as a page prints it: each field from the translation, else from
 * English (three es and two pt posts carry no read time of their own).
 */
export function mergeBlogPost(localized, en) {
    if (!localized && !en)
        return null;
    const out = {};
    for (const f of FIELDS) {
        const v = localized?.[f] ?? en?.[f];
        if (v !== undefined)
            out[f] = v;
    }
    return out;
}
/** "8 min read" and "8 мин чтения" both print as the figure and unit alone. */
export function normalizeReadTime(raw) {
    if (!raw)
        return '';
    return raw.replace(/\s*(?:de\s+)?(?:read(?:ing)?|чтения|lectura|leitura|okuma|阅读)\s*$/i, '').trim();
}
/**
 * "Keep reading": the page language's posts other than this one, the same
 * rubric first, newest first. Only translated posts are listed, so each link
 * is a real page in that language.
 */
export function relatedPosts(slug, category, catalog, n = 3) {
    return catalog
        .filter(([s]) => s !== slug)
        .map(([s, p]) => [s, p])
        .sort(([, x], [, y]) => Number(y.category === category) - Number(x.category === category)
        || (y.date ?? '').localeCompare(x.date ?? ''))
        .slice(0, n);
}
/**
 * "Contents" above a long post: its sections, numbered, each a link to its
 * heading. The markdown view leaves it out (the headings are already there).
 */
function contentsHtml(sections, label) {
    if (sections.length < TOC_MIN_SECTIONS)
        return '';
    const items = sections.map((s) => `<li><a href="#${esc(s.id)}">${esc(s.text)}</a></li>`).join('');
    return `<nav class="article-toc article-column" aria-label="${esc(label)}" data-md="skip">
        <p class="article-toc__title label">${esc(label)}</p>
        <ol class="article-toc__list">${items}</ol>
      </nav>`;
}
/** Everything inside `<article class="article-route">`. */
export function blogPostHtml(i) {
    const { t, post, locale } = i;
    const { html: bodyHtml, sections } = anchorSections(post.body ?? '');
    const h1 = post.h1 ?? post.title ?? '';
    const kicker = [post.category, normalizeReadTime(post.readTime)].filter(Boolean).map((x) => esc(x)).join(' · ');
    const hero = post.hero;
    const credit = hero ? photoCreditHtml(hero) : '';
    const heroHtml = hero
        ? `<figure class="blog-hero">
        <img src="${esc(hero.src)}" srcset="${esc(heroSrcset(hero.src))}" sizes="(max-width: 768px) calc(100vw - 64px), 1216px" alt="${esc(hero.alt)}" width="${hero.width}" height="${hero.height}" loading="eager" fetchpriority="high" decoding="async" />
        ${credit ? `<figcaption class="blog-hero__credit">${credit}</figcaption>` : ''}
      </figure>`
        : '';
    const stamp = post.lastUpdated || post.date || '';
    const updated = stamp
        ? `<p class="article-last-updated label"><time datetime="${esc(stamp)}">${esc(t('article.lastUpdated'))}: ${esc(formatPostDate(stamp, locale))}</time></p>`
        : '';
    const cards = i.related.map(([s, p]) => `<a class="related-card" href="${esc(localeHref(locale, `/blog/${s}`))}">
          ${p.category ? `<div class="related-card__cat">${esc(p.category)}</div>` : ''}
          <div class="related-card__title">${esc(p.h1 ?? p.title ?? '')}</div>
          <div class="related-card__excerpt">${esc(ledeText(p.lede ?? ''))}</div>
        </a>`).join('');
    return `
  <section class="hero article-head">
    <div class="container">
      <div class="breadcrumbs article-column">
        <a href="${esc(localeHref(locale, '/'))}">${esc(t('nav.home'))}</a> · <a href="${esc(localeHref(locale, '/blog'))}">${esc(t('nav.blog'))}</a> · <span class="breadcrumbs__current">${esc(h1)}</span>
      </div>
      <div class="article-hero article-column">
        ${kicker ? `<div class="section__kicker">${kicker}</div>` : ''}
        <h1 class="article-h1">${esc(h1)}</h1>
        ${post.lede ? `<p class="article-lede">${ledeHtml(post.lede)}</p>` : ''}
      </div>
      ${heroHtml}
    </div>
  </section>
  <section class="section article-main">
    <div class="container">
      ${updated}
      ${contentsHtml(sections, t('blogArticle.contents'))}
      <div class="article-body">${localizeBodyHrefs(bodyHtml, locale, { hasBlogTranslation: i.hasBlogTranslation })}</div>
    </div>
  </section>
  ${i.trialCtaHtml}
  ${cards ? `<section class="section article-related">
    <div class="container">
      <div class="section__kicker">${esc(t('blogArticle.keepReading'))}</div>
      <div class="related-grid">${cards}</div>
    </div>
  </section>` : ''}
`;
}
